import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@utils';
import * as React from 'react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../../atoms';
import type { TabItem, TabsProps } from './tab.type';
import { TabDirection, IconPosition } from './tab.type';

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.List>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & { direction?: TabDirection }
>(({ className, direction = TabDirection.LTR, ...props }, ref) => (
	<TabsPrimitive.List
		ref={ref}
		className={cn(
			'bg-muted text-muted-foreground inline-flex h-9 items-center justify-start rounded-md p-1',
			direction === TabDirection.RTL && 'flex-row-reverse',
			'md:flex',
			className
		)}
		dir={direction}
		{...props}
	/>
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & { tab: TabItem; direction?: TabDirection }
>(({ className, tab, direction = TabDirection.LTR, ...props }, ref) => {
	const iconWithTooltip = tab.icon && tab.tooltip && (
		<Tooltip>
			<TooltipTrigger asChild>
				<span
					className={cn(
						direction === TabDirection.LTR
							? tab.iconPosition === IconPosition.After
								? 'ml-2'
								: 'mr-2'
							: tab.iconPosition === IconPosition.After
								? 'mr-2'
								: 'ml-2'
					)}
				>
					{tab.icon}
				</span>
			</TooltipTrigger>
			{tab.tooltip && <TooltipContent>{tab.tooltip}</TooltipContent>}
		</Tooltip>
	);

	return (
		<TabsPrimitive.Trigger
			ref={ref}
			className={cn(
				'ring-offset-background focus-visible:ring-ring inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2',
				'text-secondary',
				'border-b-2 border-transparent',
				'data-[state=active]:border-b-primary data-[state=active]:text-primary',
				'rounded-b-none',
				!tab.disable && 'hover:text-primary',
				tab.disable && 'cursor-not-allowed opacity-50',
				className
			)}
			{...props}
			disabled={tab.disable}
			id={`tab-trigger-${tab.name}`} // ADDED ID HERE
			aria-controls={`tab-content-${tab.name}`}
		>
			{direction === TabDirection.LTR ? (
				<>
					{tab.iconPosition !== IconPosition.After && iconWithTooltip}
					<span>{tab.name}</span>
					{tab.iconPosition === IconPosition.After && iconWithTooltip}
				</>
			) : (
				<>
					{tab.iconPosition === IconPosition.After && iconWithTooltip}
					<span>{tab.name}</span>
					{tab.iconPosition !== IconPosition.After && iconWithTooltip}
				</>
			)}
		</TabsPrimitive.Trigger>
	);
});
TabsTrigger.displayName = 'TabsTrigger';

const TabsContent = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
	<TabsPrimitive.Content
		ref={ref}
		className={cn(
			'ring-offset-background focus-visible:ring-ring focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2',
			className
		)}
		{...props}
		aria-labelledby={`tab-trigger-${props.value}`}
	/>
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

const SmartTabs = ({
	tabs,
	className,
	defaultValue,
	keyboardNavigation = true,
	direction = TabDirection.LTR,
	ariaLabels = {},
	margin,
	beforeTabs,
	...props
}: TabsProps) => {
	const visibleTabs = tabs.filter((tab: TabItem) => tab.hidden !== true);
	const firstVisibleTab = visibleTabs.find((tab: TabItem) => !tab.disable);
	const [value, setValue] = React.useState(defaultValue || firstVisibleTab?.name || '');

	// Avoid unnecessary state updates
	React.useEffect(() => {
		if (!value && firstVisibleTab) {
			setValue(firstVisibleTab.name);
		}
	}, [value, firstVisibleTab]);

	return (
		<TooltipProvider>
			<div className={cn('flex flex-col', className)} dir={direction}>
				{/* Mobile Dropdown To fix swap issue */}
				{/* <select
					data-aid="select-tab"
					className="md:hidden block w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background focus:border-primary focus:outline-hidden focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50 appearance-none pr-8"
					value={value}
					onChange={handleSelectChange}
					aria-label={ariaLabels.tabList}
				>
					{visibleTabs.map((tab: TabItem) => (
						<option key={tab.name} value={tab.name}>
							{tab.name}
						</option>
					))}
				</select> */}

				{/* Desktop Tabs */}
				<Tabs
					data-aid="tabs"
					value={value} // Controlled value
					className={cn('flex flex-col', className)}
					onValueChange={(val: string) => setValue(val)}
					dir={direction}
					{...props}
				>
					<div className="flex items-start justify-between">
						{beforeTabs && (
							<div
								className={cn(
									'border-border flex shrink-0 items-start',
									direction === TabDirection.RTL ? 'ml-[1rem] border-l-2' : 'mr-[1rem] border-r-2'
								)}
							>
								{beforeTabs}
							</div>
						)}
						<div className="w-full">
							<TabsList
								style={{ marginBottom: margin ?? '0' }}
								aria-label={ariaLabels.tabList}
								direction={direction}
							>
								{visibleTabs.map((tab: TabItem) => (
									<TabsTrigger
										key={tab.name}
										value={tab.value || tab.name}
										disabled={tab.disable}
										tab={tab}
										direction={direction}
										aria-controls={`tab-content-${tab.name}`}
										aria-label={ariaLabels.tab}
									/>
								))}
							</TabsList>
						</div>
						{props.children && <div className="shrink-0">{props.children}</div>}
					</div>

					{/* Tab Content */}
					{visibleTabs.map((tab: TabItem) => (
						<TabsContent
							key={tab.name}
							value={tab.value || tab.name}
							id={`tab-content-${tab.name}`}
							aria-labelledby={`tab-trigger-${tab.name}`}
							aria-label={ariaLabels.tabPanel}
							className="h-full"
						>
							{tab.page}
						</TabsContent>
					))}
				</Tabs>
			</div>
		</TooltipProvider>
	);
};

export { SmartTabs as Tabs, TabsList, TabsTrigger, TabsContent, TabsProps };
